"use client";

import type React from "react";

import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Flag, Send } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";

const formSchema = (t: any) =>
  z.object({
    message: z
      .string()
      .min(50, {
        message: t("forms:errors.reportMin"),
      })
      .max(2000, {
        message: t("forms:errors.reportMax"),
      }),
  });

type FormData = z.infer<ReturnType<typeof formSchema>>;

interface ReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId?: string;
  conversationName?: string;
}

export function ReportDialog({
  isOpen,
  onClose,
  conversationId,
  conversationName,
}: ReportDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isAlreadyReported, setIsAlreadyReported] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);

  const { t } = useTranslation(["common", "chat", "forms"]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema(t)),
    defaultValues: {
      message: "",
    },
  });

  const handleSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      await axiosApiInstance.post(
        `${API_ENDPOINTS.CHAT.MANAGE_CONVERSATION}/${conversationId}/report`,
        data,
      );

      setIsSuccess(true);

      setTimeout(() => {
        onClose();
        form.reset();
        setIsSuccess(false);
      }, 5000);
    } catch (error: any) {
      if (error.response?.data?.code === "report_already_sent") {
        setIsAlreadyReported(true);

        setTimeout(() => {
          onClose();
          form.reset();
          setIsAlreadyReported(false);
        }, 5000);
      }
      console.error("Error reporting conversation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharacterCount(e.target.value.length);
    form.setValue("message", e.target.value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-foreground/90 border-border/50 max-w-md rounded-2xl shadow-2xl backdrop-blur-xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Flag className="text-destructive h-5 w-5" />
              {t("chat:report.title")}
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground pt-2 text-sm">
            {t("chat:report.description")}
            {conversationName && <span className="font-medium"> {`"${conversationName}"`}</span>}.
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {isSuccess ? (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-8 text-center"
              exit={{ opacity: 0, y: 20 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-success/10 text-success mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <Send className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-medium">{t("chat:report.successTitle")}</h3>
              <p className="text-muted-foreground max-w-xs text-sm">
                {t("chat:report.successMessage")}
              </p>
            </motion.div>
          ) : isAlreadyReported ? (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-8 text-center"
              exit={{ opacity: 0, y: 20 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/10 text-blue-500">
                <AlertCircle className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-medium">{t("chat:report.alreadyReportedTitle")}</h3>
              <p className="text-muted-foreground max-w-xs text-sm">
                {t("chat:report.alreadyReportedMessage")}
              </p>
            </motion.div>
          ) : (
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Form {...form}>
                <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-primary-foreground text-sm font-medium">
                          {t("chat:report.reasonLabel")}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <ScrollArea className="border-border/50 bg-muted/20 ring-offset-background focus-within:ring-primary/30 box-border h-40 rounded-2xl border backdrop-blur-sm focus-within:ring-2">
                              <Textarea
                                className="box-border h-full resize-none overflow-hidden border-none bg-transparent pt-3 pb-6 break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                placeholder={t("chat:report.reasonPlaceholder")}
                                {...field}
                                onChange={handleTextareaChange}
                              />
                            </ScrollArea>
                            <div className="text-muted-foreground absolute right-2 bottom-2 text-xs">
                              {characterCount}/{2000}
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="bg-muted/20 flex items-start gap-3 rounded-2xl p-4 text-sm backdrop-blur-sm">
                    <AlertCircle className="text-accent-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div className="text-muted-foreground space-y-1">
                      <p>{t("chat:report.warningReview")}</p>
                      <p>{t("chat:report.warningAbuse")}</p>
                    </div>
                  </div>

                  <DialogFooter className="sm:justify-end">
                    <Button
                      className="text-primary-foreground hover:bg-muted/50"
                      type="button"
                      variant="ghost"
                      onClick={onClose}
                    >
                      {t("common:cancel")}
                    </Button>
                    <Button
                      className={cn(
                        "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                        isSubmitting && "opacity-70",
                      )}
                      disabled={isSubmitting}
                      type="submit"
                    >
                      {isSubmitting ? t("chat:report.submitting") : t("chat:report.submit")}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
